import { apiClient } from './client';
import type { Branch, User } from '@/types/models';

// ===== Admin Types =====

export interface AdminStats {
  total_branches: number;
  branches_by_status: Record<string, number>;
  total_users: number;
  users_by_role: Record<string, number>;
  total_orders: number;
  pending_applications_count: number;
  recent_registrations: number;
}

export interface BranchApplication {
  id: number;
  applicant_name: string;
  applicant_email: string;
  applicant_phone: string | null;
  branch_name: string;
  branch_address: string;
  branch_phone: string | null;
  latitude: number | null;
  longitude: number | null;
  working_hours: Record<string, any> | null;
  status: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
}

interface BranchListResponse {
  items: Branch[];
  total: number;
}

interface UserListResponse {
  items: User[];
  total: number;
}

interface ApplicationListResponse {
  items: BranchApplication[];
  total: number;
}

// ===== Admin API =====

export const adminApi = {
  // Stats
  getStats: async (): Promise<AdminStats> =>
    (await apiClient.get<AdminStats>('/v1/admin/stats')).data,

  // Branches
  getBranches: async (params?: Record<string, any>): Promise<BranchListResponse> =>
    (await apiClient.get<BranchListResponse>('/v1/admin/branches', { params })).data,

  getBranch: async (id: number) =>
    (await apiClient.get('/v1/admin/branches/' + id)).data,

  updateBranch: async (id: number, data: any) =>
    (await apiClient.patch('/v1/admin/branches/' + id, data)).data,

  blockBranch: async (id: number) =>
    (await apiClient.post('/v1/admin/branches/' + id + '/block')).data,

  unblockBranch: async (id: number) =>
    (await apiClient.post('/v1/admin/branches/' + id + '/unblock')).data,

  // Applications
  getApplications: async (params?: Record<string, any>): Promise<ApplicationListResponse> =>
    (await apiClient.get<ApplicationListResponse>('/v1/admin/applications', { params })).data,

  getApplication: async (id: number) =>
    (await apiClient.get('/v1/admin/applications/' + id)).data,

  reviewApplication: async (id: number, data: { status: string; rejection_reason?: string }) =>
    (await apiClient.post('/v1/admin/applications/' + id + '/review', data)).data,

  // Users
  getUsers: async (params?: Record<string, any>): Promise<UserListResponse> =>
    (await apiClient.get<UserListResponse>('/v1/admin/users', { params })).data,

  getUser: async (id: number) =>
    (await apiClient.get('/v1/admin/users/' + id)).data,

  updateUser: async (id: number, data: any) =>
    (await apiClient.patch('/v1/admin/users/' + id, data)).data,

  blockUser: async (id: number) =>
    (await apiClient.post('/v1/admin/users/' + id + '/block')).data,

  unblockUser: async (id: number) =>
    (await apiClient.post('/v1/admin/users/' + id + '/unblock')).data,
};
